import { defineStore } from "pinia";
import { computed, ComputedRef, reactive, Ref, ref } from "vue";

import { LogService } from "@/services";
import {
  BriefcaseFolder,
  BriefcaseItem,
  BriefcasePayload,
  SearchItem,
} from "@/types";

interface BriefcaseMeta {
  [key: string]: number | boolean | null | SearchItem | Date;
  defaultFolderId: number;
  currentFolderId: number;
  currentPage: number;
  pageSize: number;
  isLoading: boolean;
  lastUpdated: Date | null;
  itemBeingMoved: SearchItem | null;
}

interface BriefcaseFolders {
  [key: number]: BriefcaseFolder;
}

/**
 * Structure for defining the items a user has in their briefcase with a O(1) lookup.
 * Takes a briefcaseItemId and returns the folderIds that the item is in.
 */
interface BriefcaseItemIndex {
  [key: string]: number[];
}

/**
 * Interface for the params of the addItemIndexEntry and removeItemIndexEntry actions.
 */
interface EditItemIndexParams {
  id: number;
  newFolderId: number;
}

const metaDefaultValues: BriefcaseMeta = {
  defaultFolderId: -1,
  currentFolderId: -1,
  currentPage: 1,
  pageSize: 12,
  isLoading: false,
  lastUpdated: null,
  itemBeingMoved: null,
};

export const useBriefcaseStore = defineStore("briefcase", () => {
  const folders: Ref<BriefcaseFolders> = ref({});
  const itemIndex: Ref<BriefcaseItemIndex> = ref({});

  const itemIds = ref<number[]>([]);
  const isInBriefcase = (n: number) =>
    computed(() => itemIds.value.includes(n));

  const flatFolders: Ref<BriefcaseFolder[]> = ref([]);
  const flatFoldersSorted = computed(() => {
    const folders = flatFolders.value
      .slice(1)
      .sort((a, b) => a.name.localeCompare(b.name));
    if (flatFolders.value[0]) {
      folders.unshift(flatFolders.value[0]);
    }
    return folders;
  });

  /**
   * Reactive object that holds meta information related to a briefcase.
   *
   * @typedef {Object} BriefcaseMeta
   *
   * @property {number} defaultFolderId - The default folder ID for the briefcase.
   * @property {number} currentFolderId - The ID of the current folder being displayed.
   * @property {number} currentPage - The current page number being displayed.
   * @property {number} pageSize - The number of items to display per page.
   * @property {boolean} isLoading - Indicates whether the briefcase is currently being loaded.
   * @property {Date|null} lastUpdated - The date and time of the last update to the briefcase, or null if never updated.
   * @property {any|null} itemBeingMoved - The item that is currently being moved, or null if no item is being moved.
   */
  const meta: BriefcaseMeta = reactive({
    defaultFolderId: -1,
    currentFolderId: -1,
    currentPage: 1,
    pageSize: 12,
    isLoading: false,
    lastUpdated: null,
    itemBeingMoved: null,
  });

  /**
   * Resets the meta object to its default values.
   *
   * @function resetMetaToDefaults
   */
  const resetMetaToDefaults = () => {
    for (const key in metaDefaultValues) {
      meta[key] = metaDefaultValues[key] ?? null;
    }
  };

  /**
   * Checks if an item with the specified ID has been added to the briefcase.
   * @param {number} id - The ID of the item.
   * @returns {ComputedRef<boolean>} - A computed reference indicating if the item has been added to the briefcase.
   */
  const isAddedToBriefcaseById = (id: number): boolean =>
    itemIndex.value[id] !== undefined;

  /**
   * Returns whether the briefcase item is in a folder
   * @param id
   * @returns boolean
   */
  const hasValidFolderById = (id: number): ComputedRef<boolean> =>
    computed(() => {
      return folders.value[id] !== undefined;
    });

  /**
   * Returns whether the name of the briefcase folder is unique
   * @param name
   * @returns boolean
   */
  const isNewFolderUnique = (name: string): ComputedRef<boolean> =>
    computed(() => {
      return (
        Object.values(folders.value).findIndex(
          (folder) => folder.name === name
        ) === -1
      );
    });

  /**
   * Returns the folder id of the item with the given briefcase item id
   * @param id
   * @returns number
   */
  const getFolderIdById = (id: number): ComputedRef<number> =>
    computed(() => {
      const folders = itemIndex.value[id];
      return folders?.[0] ?? -1;
    });

  /**
   * Retrieves folder IDs for a given ID.
   *
   * @param {number} id - The ID for which to retrieve the folder IDs.
   * @returns {ComputedRef<number[]>} - The computed reference to an array of folder IDs.
   */
  const getFolderIdsById = (id: number): ComputedRef<number[]> =>
    computed(() => {
      return itemIndex.value[id] || [];
    });

  /**
   * Retrieves an array of folder IDs for an item by its ID.
   * @param {number} id - The ID of the item.
   * @returns {ComputedRef<number[]>} - Computed reference to an array of folder IDs.
   */
  const getAllFolderIdsForItem = (id: number): ComputedRef<number[]> =>
    computed(() => {
      return itemIndex.value[id] ?? [];
    });

  /**
   * Returns the briefcase folder object with current folder id
   * found in meta.currentFolderId
   *
   * @returns {ComputedRef<BriefcaseFolder>} - The ID of the current folder.
   */
  const getCurrentFolder = (): ComputedRef<BriefcaseFolder | null> =>
    computed(() => {
      return folders.value[meta.currentFolderId] ?? null;
    });

  /**
   * Retrieves the current dossier URL from the current briefcase folder.
   *
   * @returns {ComputedRef<string>} The computed reference to the current dossier URL.
   */
  const getCurrentDossierUrl = (): ComputedRef<string> =>
    computed(() => {
      const currentFolder = folders.value[meta.currentFolderId];
      if (currentFolder) {
        return currentFolder.dossierUrl;
      }
      return "";
    });

  /**
   * Returns the name of the current folder.
   *
   * @returns {ComputedRef<string>} The name of the current folder.
   */
  const getCurrentFolderName = (): ComputedRef<string> =>
    computed(() => {
      const currentFolder = folders.value[meta.currentFolderId];
      if (currentFolder) {
        return currentFolder.name;
      }
      return "";
    });

  /**
   * Retrieves the items of the current folder.
   *
   * @returns {ComputedRef<BriefcaseItem[]>} - A computed reference to an array of
   * BriefcaseItem objects representing the items in the current folder.
   */
  const getCurrentFolderItems = (): ComputedRef<BriefcaseItem[]> =>
    computed(() => {
      return folders.value[meta.currentFolderId]?.items ?? [];
    });

  /**
   * Retrieves the count of items in the current folder.
   * @returns {ComputedRef<number>} - The computed reference to the number of items in the current folder.
   */
  const getCurrentFolderItemsCount = (): ComputedRef<number> =>
    computed(() => {
      const currentFolder = folders.value[meta.currentFolderId];
      if (currentFolder) {
        return currentFolder.items.length;
      }
      return 0;
    });

  /**
   * Computes the total number of pages for the current folder.
   * @returns {ComputedRef<number>} - A ComputedRef containing the current folder's page count.
   */
  const getCurrentFolderPageCount = (): ComputedRef<number> =>
    computed(() => {
      const currentFolder = folders.value[meta.currentFolderId];
      if (currentFolder) {
        return Math.ceil(currentFolder.items.length / meta.pageSize);
      }
      return 0;
    });

  /**
   * Retrieves the current page items to display for the current folder.
   * @returns {ComputedRef<BriefcaseItem[]>} The computed reference to the current page items.
   */
  const getCurrentFolderCurrentPageItems = (): ComputedRef<BriefcaseItem[]> =>
    computed(() => {
      const start = (meta.currentPage - 1) * meta.pageSize;
      const end = start + meta.pageSize;
      return folders.value[meta.currentFolderId]?.items.slice(start, end) ?? [];
    });

  /**
   * Sets the briefcase folders.
   *
   * @param {BriefcaseFolder[]} newFolders - The new folders to set.
   * @return {void}
   */
  function setBriefcaseFolders(newFolders: BriefcaseFolder[]): void {
    // check for bad values
    if (newFolders == null || newFolders.length === 0) {
      return;
    }

    // see if the current folder is not in the list of folders to set
    const currentFolderNotInNewList =
      newFolders.findIndex((folder) => folder.id === meta.currentFolderId) ===
      -1;
    if (meta.currentFolderId === -1 && currentFolderNotInNewList) {
      meta.currentFolderId = -1;
      meta.currentPage = 1;
    }

    newFolders.forEach((folder) => {
      folders.value[folder.id] = folder;
    });

    itemIndex.value = {};

    Object.values(folders.value).forEach((folder: BriefcaseFolder) => {
      folder.items.forEach((item) => {
        const folderIds = itemIndex.value[item.briefcaseItemId] ?? [];
        if (folderIds.indexOf(folder.id) === -1) {
          folderIds.push(folder.id);
        }
        itemIndex.value[item.briefcaseItemId] = folderIds;
      });
      if (folder.isDefault) {
        meta.defaultFolderId = folder.id;

        if (meta.currentFolderId === -1) {
          meta.currentFolderId = folder.id;
        }
      }
    });

    if (meta.currentFolderId === -1 && newFolders[0]) {
      meta.currentFolderId = meta.defaultFolderId || newFolders[0].id;
    }
    flatFolders.value = Object.values(folders.value);
  }

  function addFolder(folder: BriefcaseFolder): void {
    folders.value[folder.id] = folder;

    flatFolders.value = Object.values(folders.value);
  }

  function removeFolder(folderId: number): void {
    delete folders.value[folderId];

    flatFolders.value = Object.values(folders.value);
  }

  /**
   * Adds items to the briefcase.
   *
   * @param {BriefcasePayload} params - The payload containing the items to be added to the briefcase and the folder ID to add them to.
   */
  function addToBriefcase(params: BriefcasePayload): void {
    // if the user did not select a folder, use the default folder "General Briefcase"
    if (params.folderId === undefined) {
      params.folderId = meta.defaultFolderId;
    }

    // get the folder object (either the user specific folder or the default folder)
    const briefcaseFolder = folders.value[params.folderId];

    // if the folder is not found, log a warning and return because something happened that should not have.
    if (briefcaseFolder === undefined) {
      LogService.error(`addToBriefcase: folderId ${params.folderId} not found`);
      return;
    } else {
      // get the IDs of the items in the folder
      const profileIds = briefcaseFolder.items.map(
        (item) => item.briefcaseItemId
      );

      params.items.forEach((item) => {
        // if the item is not in the folder, add it to the front of the folder
        const itemBriefcaseIdNotInProfileIds =
          profileIds.indexOf(item.briefcaseItemId) === -1;
        if (itemBriefcaseIdNotInProfileIds) briefcaseFolder.items.unshift(item);

        // get the IDs of the folders that the item is in
        const folderIds = itemIndex.value[item.briefcaseItemId] ?? [];
        const folderIdNotInFolderIds =
          folderIds.indexOf(briefcaseFolder.id) === -1;
        // if the item is not in the folder, add the folder to the folderIds array
        if (folderIdNotInFolderIds) folderIds.push(briefcaseFolder.id);
        // update the itemIndex object
        itemIndex.value[item.briefcaseItemId] = folderIds;
        itemIds.value.push(item.briefcaseItemId);
      });
    }
  }

  /**
   * Removes items from the briefcase.
   *
   * @param params The payload containing folderId and items to be removed.
   * @returns {void}
   */
  function removeFromBriefcase(params: BriefcasePayload): void {
    params.folderId = params.folderId ?? meta.defaultFolderId;

    const briefcaseFolder = folders.value[params.folderId];

    if (briefcaseFolder === undefined) {
      LogService.warn(
        `removeFromBriefcase: folderId ${params.folderId} not found`
      );
      return;
    } else {
      for (const item of params.items) {
        briefcaseFolder.items = briefcaseFolder.items.filter(
          (briefcaseItem) =>
            briefcaseItem.briefcaseItemId !== item.briefcaseItemId
        );

        let folderIds: number[] = itemIndex.value[item.briefcaseItemId] ?? [];
        folderIds = folderIds.filter(
          (folderId) => folderId !== params.folderId
        );
        if (folderIds.length === 0) {
          delete itemIndex.value[item.briefcaseItemId];
        } else {
          itemIndex.value[item.briefcaseItemId] = folderIds;
        }
        itemIds.value = itemIds.value.filter(
          (id) => id !== item.briefcaseItemId
        );
      }
    }
  }

  /**
   * Adds an index entry for an item in the item index.
   *
   * @param id - The ID of the item.
   * @param newFolderId - The ID of the new folder to be added to the item's index entry.
   *
   * @return {void}
   */
  function addItemIndexEntry({ id, newFolderId }: EditItemIndexParams): void {
    let folderIds: number[] | undefined = itemIndex.value[id];
    if (folderIds === undefined) {
      folderIds = [newFolderId];
    } else if (folderIds.indexOf(newFolderId) === -1) {
      folderIds.push(newFolderId);
    }
    itemIndex.value[id] = folderIds;
    itemIds.value.push(id);
  }

  /**
   * Removes the specified folder id from the item index entry.
   *
   * @param {number} id - The id of the item.
   * @param {number} newFolderId - The id of the folder to remove.
   * @return {void}
   */
  function removeItemIndexEntry({
    id,
    newFolderId,
  }: EditItemIndexParams): void {
    // get the folder ids for the item
    let folderIds: number[] | undefined = itemIndex.value[id] ?? [];
    // remove the folder id from the array
    folderIds = folderIds.filter((folderId) => folderId !== newFolderId);
    // if the array is empty, delete the item index entry, else set the new folder ids that are w/o that folder id
    if (folderIds.length === 0) {
      delete itemIndex.value[id];
    } else {
      itemIndex.value[id] = folderIds;
    }
    itemIds.value = itemIds.value.filter((itemId) => itemId !== id);
  }

  function setLightweightBriefcaseData(
    items: number[],
    folders: BriefcaseFolder[]
  ): void {
    setBriefcaseFolders(folders);
    itemIds.value = items;
  }

  return {
    folders,
    flatFolders,
    flatFoldersSorted,
    isInBriefcase,
    itemIndex,
    itemIds,
    meta,
    resetMetaToDefaults,
    isAddedToBriefcaseById,
    hasValidFolderById,
    isNewFolderUnique,
    getFolderIdById,
    getFolderIdsById,
    getAllFolderIdsForItem,
    getCurrentDossierUrl,
    getCurrentFolder,
    getCurrentFolderName,
    getCurrentFolderItems,
    getCurrentFolderItemsCount,
    getCurrentFolderPageCount,
    getCurrentFolderCurrentPageItems,
    setBriefcaseFolders,
    setLightweightBriefcaseData,
    addFolder,
    removeFolder,
    addToBriefcase,
    removeFromBriefcase,
    addItemIndexEntry,
    removeItemIndexEntry,
  };
});
